import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Play, Plus, CalendarClock, X } from 'lucide-react'
import {
  addProductWebsiteUrl,
  createAndApproveAllVariantPcrs,
  ensureExtractionProfileForProduct,
  fetchPcrRecords,
  fetchProductWebsiteUrls,
  fetchProducts,
  fetchRulePacks,
  type ProductWebsiteUrlCategory,
  type ProductWebsiteUrlDto,
} from '../services/api'
import {
  createComplianceRun,
  createSpotcheckSchedule,
  fetchComplianceRunPreflight,
} from '../services/complianceV2'
import { ExtractionProfileGenerate } from './ExtractionProfileGenerate'
import { EmptyState, MimCard, MimPage, StatusPill } from '../components/MimUi'
import { useAuth } from '../context/AuthContext'
import { TARGET_SECTION_OPTIONS, parseTargetSections, type TargetSectionCode } from '../utils/targetSection'
import './RunChecks.css'

const DEFAULT_PAGE_SECTIONS: TargetSectionCode[] = ['Marketing', 'TechnicalData', 'Downloads']

function SectionChecks({
  selected,
  onChange,
  disabled,
}: {
  selected: TargetSectionCode[]
  onChange: (next: TargetSectionCode[]) => void
  disabled?: boolean
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
      {TARGET_SECTION_OPTIONS.map((option) => {
        const checked = selected.includes(option.code)
        return (
          <label key={option.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled || (checked && selected.length === 1)}
              onChange={() => {
                const next = checked
                  ? selected.filter((code) => code !== option.code)
                  : [...selected, option.code]
                if (next.length > 0) onChange(next)
              }}
            />
            <span>{option.label}</span>
          </label>
        )
      })}
    </div>
  )
}

function ownerCategoryLabel(category?: string) {
  switch (category) {
    case 'KingspanMarketing': return 'Internal owned'
    case 'Distributor': return 'Accredited distributor'
    case 'OtherSeller': return 'Independent supplier'
    default: return category || 'Owner'
  }
}

type StepId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
/** Steps 6 (rule pack) and 8 (AI ready) are informational and never the current incomplete step. */
type CurrentStepId = Exclude<StepId, 6 | 8>

function StepBadge({ n, active, done, label }: { n: number; active: boolean; done: boolean; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 8,
        background: done ? 'var(--mim-success-bg)' : active ? 'var(--mim-blue-pale)' : 'var(--mim-grey-bg)',
        border: `1px solid ${done ? 'var(--mim-success)' : active ? 'var(--mim-blue)' : 'var(--mim-border)'}`,
        color: done ? 'var(--mim-success)' : 'var(--mim-text)',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {done ? <CheckCircle2 size={16} /> : <span style={{ width: 18, textAlign: 'center' }}>{n}</span>}
      <span>{label}</span>
    </div>
  )
}

export default function WebsiteCheckWizard() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const initialProductId = searchParams.get('productId') || ''
  const [productSearch, setProductSearch] = useState('')
  const [productId, setProductId] = useState(initialProductId)
  const [sourceOfTruthRecordId, setSourceOfTruthRecordId] = useState('')
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([])
  const [languageOverride, setLanguageOverride] = useState('en')
  const [rulePackId, setRulePackId] = useState('')
  const [showExtractionModal, setShowExtractionModal] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    frequency: 'Weekly',
    dayOfWeek: 1,
    dayOfMonth: 1,
    hourUtc: 8,
    minuteUtc: 0,
    ownerEmail: user?.email || '',
  })
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [urlDraft, setUrlDraft] = useState({
    category: 'Distributor' as ProductWebsiteUrlCategory,
    contentTypes: DEFAULT_PAGE_SECTIONS,
    url: '',
    displayName: '',
  })
  const [sectionSavingId, setSectionSavingId] = useState<string | null>(null)
  const [showAddUrl, setShowAddUrl] = useState(false)

  useEffect(() => {
    const fromUrl = searchParams.get('productId') || ''
    if (fromUrl && fromUrl !== productId) {
      setProductId(fromUrl)
      setSourceOfTruthRecordId('')
      setSelectedTargetIds([])
      setRulePackId('')
      setError(null)
    }
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'website-check'],
    // Include PCR-imported products even when Kingspan URL is not set yet.
    queryFn: () => fetchProducts(),
  })

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === productId) || null,
    [products, productId],
  )

  useEffect(() => {
    if (selectedProduct && !productSearch) {
      setProductSearch(`${selectedProduct.code} - ${selectedProduct.name}`)
    }
  }, [selectedProduct]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase()
    let list = !q
      ? products.slice(0, 50)
      : products
          .filter((p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q))
          .slice(0, 50)
    // Always keep the URL/selected product in the dropdown options.
    if (productId && selectedProduct && !list.some((p) => p.id === productId)) {
      list = [selectedProduct, ...list].slice(0, 51)
    }
    return list
  }, [products, productSearch, productId, selectedProduct])

  const { data: approvedPcrs = [], refetch: refetchPcrs } = useQuery({
    queryKey: ['pcr-records', productId, 'approved'],
    queryFn: () => fetchPcrRecords({ productId, status: 'Approved' }),
    enabled: !!productId,
  })

  // Website Check uses one PCR revision for the run. Prefer product-level (no variant),
  // otherwise the most recently approved snapshot — do not list every thickness variant.
  const primaryPcr = useMemo(() => {
    if (approvedPcrs.length === 0) return null
    const noVariant = approvedPcrs.find((p) => !p.productVariantId)
    if (noVariant) return noVariant
    return [...approvedPcrs].sort((a, b) => {
      const aUtc = a.approvedUtc || a.createdUtc || ''
      const bUtc = b.approvedUtc || b.createdUtc || ''
      return bUtc.localeCompare(aUtc)
    })[0]
  }, [approvedPcrs])

  const { data: websiteUrls = [], refetch: refetchUrls } = useQuery({
    queryKey: ['product-website-urls', productId],
    queryFn: () => fetchProductWebsiteUrls(productId),
    enabled: !!productId,
  })

  const { data: rulePacks = [] } = useQuery({
    queryKey: ['rule-packs', 'website-check'],
    queryFn: () => fetchRulePacks(),
  })

  const approvedPacks = useMemo(
    () => rulePacks.filter((p) => p.status === 'Approved'),
    [rulePacks],
  )
  const packsForLanguage = useMemo(
    () =>
      approvedPacks.filter(
        (p) => !p.languageCode || p.languageCode.toLowerCase() === languageOverride.toLowerCase(),
      ),
    [approvedPacks, languageOverride],
  )
  const englishPacks = useMemo(
    () => approvedPacks.filter((p) => !p.languageCode || p.languageCode.toLowerCase() === 'en'),
    [approvedPacks],
  )
  const selectablePacks = packsForLanguage.length > 0 ? packsForLanguage : englishPacks
  const usingEnglishFallback = packsForLanguage.length === 0 && englishPacks.length > 0
  const coreComparisonOnly = selectablePacks.length === 0

  const { data: preflight } = useQuery({
    queryKey: ['compliance-preflight', productId, sourceOfTruthRecordId, rulePackId],
    queryFn: () => fetchComplianceRunPreflight(productId),
    enabled: !!productId,
  })

  useEffect(() => {
    if (primaryPcr && sourceOfTruthRecordId !== primaryPcr.id) {
      setSourceOfTruthRecordId(primaryPcr.id)
    }
  }, [primaryPcr, sourceOfTruthRecordId])

  useEffect(() => {
    if (selectablePacks.length === 0) {
      if (rulePackId) setRulePackId('')
      return
    }
    if (!rulePackId || !selectablePacks.some((p) => p.id === rulePackId)) {
      setRulePackId(selectablePacks[0].id)
    }
  }, [selectablePacks, rulePackId])

  const step1Done = !!productId
  const step2Done = !!primaryPcr && !!sourceOfTruthRecordId
  const step3Done = selectedTargetIds.length > 0
  const step4Done = !!preflight && !preflight.blockingIssues?.some((i) => i.code === 'EXTRACTION_PROFILE_MISSING')
  const step5Done = !!languageOverride
  const fieldCount = preflight?.monitoredFieldCount ?? 0
  const step7Done = fieldCount > 0
  const step6Done = step5Done
  const aiReady = !!preflight?.aiConfigured
  const step8Done = step7Done
  const canStartCheck = step1Done && step2Done && step3Done && step4Done && step5Done && step7Done
  const maxStep: CurrentStepId = !step1Done
    ? 1
    : !step2Done
      ? 2
      : !step3Done
        ? 3
        : !step4Done
          ? 4
          : !step5Done
            ? 5
            : !step7Done
              ? 7
              : 9

  const createAllVariants = useMutation({
    mutationFn: () =>
      createAndApproveAllVariantPcrs({
        productId,
        initiatedBy: user?.displayName || user?.email,
        justification: 'Confirmed all-variants PCR audit snapshot for website check',
      }),
    onSuccess: async (result) => {
      const created = result.createdOrApproved ?? []
      const errors = result.errors ?? []
      if (created.length === 0) {
        setError(
          errors.length > 0
            ? `No PCR snapshots were created. ${errors.join('; ')}`
            : 'No PCR snapshots were created. Check that this product has Kingspan/PIM attribute values for at least one variant.',
        )
        setMessage(null)
      } else {
        setMessage(
          created.length === 1
            ? 'Approved PCR snapshot ready.'
            : `Approved PCR ready (${created.length} variant snapshots created; one selected for this check).`,
        )
        setError(null)
        // Seed cache immediately so step 2 / 3 appear without waiting on a scoped refetch race.
        qc.setQueryData(['pcr-records', productId, 'approved'], created)
        const preferred = created.find((p) => !p.productVariantId) || created[0]
        setSourceOfTruthRecordId(preferred.id)
      }
      await refetchPcrs()
      qc.invalidateQueries({ queryKey: ['compliance-preflight', productId] })
    },
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Failed to create PCR snapshots'),
  })

  const ensureExtraction = useMutation({
    mutationFn: () => ensureExtractionProfileForProduct(productId),
    onSuccess: async (created) => {
      setMessage(
        created.alreadyExisted
          ? 'Extraction report already in place.'
          : `Extraction report ready (${created.fieldCount ?? 0} fields).`,
      )
      setError(null)
      await qc.invalidateQueries({ queryKey: ['compliance-preflight', productId] })
    },
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Could not create the extraction report'),
  })

  const addUrl = useMutation({
    mutationFn: () =>
      addProductWebsiteUrl(productId, {
        category: urlDraft.category,
        contentTypes: urlDraft.contentTypes,
        url: urlDraft.url,
        displayName: urlDraft.displayName || undefined,
      }),
    onSuccess: async () => {
      setShowAddUrl(false)
      setUrlDraft({ category: 'Distributor', contentTypes: DEFAULT_PAGE_SECTIONS, url: '', displayName: '' })
      await refetchUrls()
    },
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Failed to add URL'),
  })

  const updateSections = useMutation({
    mutationFn: (input: { url: ProductWebsiteUrlDto; contentTypes: TargetSectionCode[] }) =>
      addProductWebsiteUrl(productId, {
        category: input.url.category,
        url: input.url.url,
        contentTypes: input.contentTypes,
      }),
    onSuccess: async () => {
      setSectionSavingId(null)
      await refetchUrls()
    },
    onError: (e: any) => {
      setSectionSavingId(null)
      setError(e?.response?.data?.message || e?.message || 'Failed to update page sections')
    },
  })

  const runNow = useMutation({
    mutationFn: () =>
      createComplianceRun({
        productId,
        sourceOfTruthRecordId,
        rulePackId: rulePackId || undefined,
        targetIds: selectedTargetIds,
        languageOverride,
      }),
    onSuccess: (run) => {
      setMessage(`Check started — opening progress…`)
      qc.invalidateQueries({ queryKey: ['check-runs'] })
      navigate(`/check-runs/${run.id}`)
    },
    onError: (e: any) => {
      const status = e?.response?.status
      const apiMessage = e?.response?.data?.message || e?.response?.data?.detail
      if (status === 403) {
        setError(apiMessage || 'Access denied for this product or one of the selected URLs. Check your org profile scope and try again.')
      } else {
        setError(apiMessage || e?.message || 'Run failed')
      }
    },
  })

  const scheduleMut = useMutation({
    mutationFn: () =>
      createSpotcheckSchedule({
        name: scheduleForm.name || `Check ${products.find((p) => p.id === productId)?.code || productId}`,
        ownerEmail: scheduleForm.ownerEmail,
        frequency: scheduleForm.frequency,
        dayOfWeek: scheduleForm.frequency === 'Weekly' ? scheduleForm.dayOfWeek : undefined,
        dayOfMonth: scheduleForm.frequency === 'Monthly' ? scheduleForm.dayOfMonth : undefined,
        hourUtc: scheduleForm.hourUtc,
        minuteUtc: scheduleForm.minuteUtc,
        rulePackId: rulePackId || undefined,
        productIds: [productId],
        targetIds: selectedTargetIds,
      }),
    onSuccess: () => {
      setShowSchedule(false)
      setMessage('Schedule created.')
    },
    onError: (e: any) => setError(e?.response?.data?.message || e?.message || 'Schedule failed'),
  })

  const toggleUrl = (id: string) => {
    setSelectedTargetIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const fieldErrorHint = !step2Done
    ? 'Step 2: approved PCR missing'
    : !step4Done
      ? 'Step 4: extraction report missing'
      : 'Approved PCR has no fields to compare'

  return (
    <MimPage title="Website Check" compactHeader>
      <div className="mim-stack website-check-wizard">
        <p className="mim-help-text" style={{ margin: 0 }}>
          Map one product to one page, then run now or schedule. Upload the Excel master on{' '}
          <Link to="/pcr-upload">PCR Upload</Link> first if the product is not in the list.
        </p>
        <div className="website-check-preflight-banner">
          <div className="website-check-preflight-steps">
            <StepBadge n={1} active={maxStep === 1} done={step1Done} label="Product" />
            <StepBadge n={2} active={maxStep === 2} done={step2Done} label="Approved PCR" />
            <StepBadge n={3} active={maxStep === 3} done={step3Done} label="URLs" />
            <StepBadge n={4} active={maxStep === 4} done={step4Done} label="Extraction" />
            <StepBadge n={5} active={maxStep === 5} done={step5Done} label="Language" />
            <StepBadge n={6} active={false} done={step6Done} label="Rule pack" />
            <StepBadge n={7} active={maxStep === 7} done={step7Done} label="Fields" />
            <StepBadge n={8} active={false} done={step8Done} label="AI ready" />
            <StepBadge n={9} active={maxStep === 9} done={false} label="Run" />
          </div>
          {(message || error) && (
            <div className="website-check-preflight-alerts">
              {message && <div className="mim-note">{message}</div>}
              {error && <div className="mim-note" style={{ background: 'var(--mim-danger-bg)', color: 'var(--mim-danger)' }}>{error}</div>}
            </div>
          )}
        </div>

        <MimCard title="1. Select PCR product">
          <input
            className="mim-input"
            placeholder="Search product name or code…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <select
            className="mim-select"
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value)
              setSourceOfTruthRecordId('')
              setSelectedTargetIds([])
              setRulePackId('')
              setError(null)
            }}
          >
            <option value="">Select a product…</option>
            {filteredProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </MimCard>

        {step1Done && (
          <MimCard title="2. Approved PCR">
            {primaryPcr ? (
              <div className="mim-stack" style={{ gap: 8 }}>
                <StatusPill status="Approved PCR ready" tone="pass" />
                <div className="mim-note">
                  Using snapshot <strong>v{primaryPcr.version}</strong>
                  {approvedPcrs.length > 1
                    ? ` (${approvedPcrs.length} variant snapshots on file; one is used for this check).`
                    : '.'}
                </div>
              </div>
            ) : (
              <>
                <p className="mim-note">
                  No approved PCR snapshot yet. If you just uploaded Excel, create the approved snapshot from those facts, then add the product page URL.
                </p>
                <button
                  type="button"
                  className="mim-btn mim-btn-primary"
                  disabled={createAllVariants.isPending}
                  onClick={() => {
                    if (window.confirm('Create and approve PCR snapshots for all variants of this product?')) {
                      createAllVariants.mutate()
                    }
                  }}
                >
                  {createAllVariants.isPending ? 'Creating…' : 'Create & approve all variant PCRs'}
                </button>
              </>
            )}
          </MimCard>
        )}

        {step2Done && (
          <MimCard
            title="3. Website URLs"
            actions={
              <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowAddUrl(true)}>
                <Plus size={14} /> Add URL
              </button>
            }
          >
            {websiteUrls.length === 0 ? (
              <EmptyState title="No URLs yet" detail="Add the product page to check. One URL can cover marketing, technical data, and downloads." />
            ) : (
              <div className="mim-stack">
                {websiteUrls.map((u: ProductWebsiteUrlDto) => (
                  <div key={u.id} className="pcr-sheet-row" style={{ alignItems: 'flex-start' }}>
                    <input
                      type="checkbox"
                      checked={selectedTargetIds.includes(u.targetId)}
                      onChange={() => toggleUrl(u.targetId)}
                      aria-label={`Include ${u.label || u.url}`}
                      style={{ marginTop: 4 }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <strong>{u.label || u.url}</strong>
                      <div className="mim-help-text" style={{ marginTop: 2 }}>{u.url}</div>
                      <div style={{ marginTop: 8 }}>
                        <SectionChecks
                          selected={parseTargetSections(u.contentType)}
                          disabled={sectionSavingId === u.id}
                          onChange={(contentTypes) => {
                            setSectionSavingId(u.id)
                            updateSections.mutate({ url: u, contentTypes })
                          }}
                        />
                      </div>
                    </div>
                    <span className="mim-help-text">{ownerCategoryLabel(u.category)}</span>
                  </div>
                ))}
              </div>
            )}
          </MimCard>
        )}

        {step2Done && step3Done && (
          <MimCard title="4. Extraction report">
            {step4Done ? (
              <StatusPill status="Extraction profile ready" tone="pass" />
            ) : (
              <div className="mim-stack" style={{ gap: 10 }}>
                <p className="mim-note" style={{ margin: 0 }}>
                  Needed once so the checker knows which PCR fields to read from the page.
                </p>
                <div className="mim-toolbar">
                  <button
                    type="button"
                    className="mim-btn mim-btn-primary"
                    disabled={ensureExtraction.isPending}
                    onClick={() => ensureExtraction.mutate()}
                  >
                    {ensureExtraction.isPending ? 'Creating…' : 'Create extraction report'}
                  </button>
                  <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowExtractionModal(true)}>
                    Review fields
                  </button>
                </div>
              </div>
            )}
          </MimCard>
        )}

        {step2Done && step3Done && step4Done && (
          <MimCard title="5. Language">
            <p className="mim-help-text" style={{ marginTop: 0 }}>
              Choose the language of the page. Implied wording may come back as Needs review — that is for you to decide, not an automatic fail.
            </p>
            <select className="mim-select" value={languageOverride} onChange={(e) => { setLanguageOverride(e.target.value); setRulePackId('') }}>
              <option value="en">EN</option>
              <option value="fr">FR</option>
              <option value="de">DE</option>
              <option value="nl">NL</option>
              <option value="es">ES</option>
              <option value="it">IT</option>
              <option value="pl">PL</option>
            </select>
          </MimCard>
        )}

        {step2Done && step3Done && step4Done && step5Done && (
          <MimCard title="6. Rule pack">
            {coreComparisonOnly ? (
              <div className="mim-note">
                No approved rule pack yet. This check will compare PCR fields only. Optional language packs can be added later in the{' '}
                <Link to="/rules-engine">Rules Engine</Link>.
              </div>
            ) : (
              <>
                {usingEnglishFallback && (
                  <div className="mim-note">
                    No approved <strong>{languageOverride.toUpperCase()}</strong> pack. Using the English pack. PCR field comparison still runs.
                  </div>
                )}
                <select className="mim-select" value={rulePackId} onChange={(e) => setRulePackId(e.target.value)}>
                  {selectablePacks.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.languageCode ? `(${p.languageCode})` : ''}
                    </option>
                  ))}
                </select>
              </>
            )}
          </MimCard>
        )}

        {step2Done && step3Done && step4Done && step5Done && (
          <MimCard title="7. Fields to check">
            {fieldCount > 0 ? (
              <StatusPill status={`${fieldCount} fields will be checked`} tone="pass" />
            ) : (
              <div className="mim-note" style={{ background: 'var(--mim-danger-bg)' }}>
                0 fields recorded. Error in a prior step — {fieldErrorHint}. Go back and fix that step.
              </div>
            )}
          </MimCard>
        )}

        {step2Done && step3Done && step4Done && step5Done && step7Done && (
          <MimCard title="8. AI readiness">
            {aiReady ? (
              <StatusPill status="AI ready" tone="pass" />
            ) : (
              <div className="mim-note">
                Azure OpenAI is not ready. Exact PCR numbers still compare. Soft wording may come back as Needs review or a technical error until AI is configured.
              </div>
            )}
          </MimCard>
        )}

        {canStartCheck && (
          <MimCard title="9. Run or schedule">
            <div className="mim-toolbar">
              <button
                type="button"
                className="mim-btn mim-btn-primary"
                disabled={runNow.isPending}
                onClick={() => runNow.mutate()}
              >
                <Play size={14} /> {runNow.isPending ? 'Starting…' : 'Run now'}
              </button>
              <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowSchedule(true)}>
                <CalendarClock size={14} /> Schedule
              </button>
            </div>
          </MimCard>
        )}
      </div>

      {showAddUrl && (
        <div className="mim-modal-backdrop">
          <div className="mim-modal">
            <div className="mim-modal-header">
              <h2>Add URL</h2>
              <button type="button" className="mim-icon-button" onClick={() => setShowAddUrl(false)}><X size={16} /></button>
            </div>
            <div className="mim-modal-body mim-stack">
              <div>
                <label className="mim-label">Owner type</label>
                <select className="mim-select" value={urlDraft.category} onChange={(e) => setUrlDraft((d) => ({ ...d, category: e.target.value as ProductWebsiteUrlCategory }))}>
                  <option value="KingspanMarketing">Internal Owned</option>
                  <option value="Distributor">Accredited Distributor</option>
                  <option value="OtherSeller">Independent Supplier</option>
                </select>
              </div>
              <div>
                <span className="mim-label">Page sections</span>
                <p className="mim-help-text" style={{ marginTop: 0 }}>
                  Select every section this URL covers. A Kingspan product page is often marketing, technical data, and downloads on the same address.
                </p>
                <SectionChecks
                  selected={urlDraft.contentTypes}
                  onChange={(contentTypes) => setUrlDraft((d) => ({ ...d, contentTypes }))}
                />
              </div>
              <div>
                <label className="mim-label">URL</label>
                <input className="mim-input" placeholder="https://…" value={urlDraft.url} onChange={(e) => setUrlDraft((d) => ({ ...d, url: e.target.value }))} />
              </div>
              <div>
                <label className="mim-label">Display name (optional)</label>
                <input className="mim-input" placeholder="e.g. Technical data sheet" value={urlDraft.displayName} onChange={(e) => setUrlDraft((d) => ({ ...d, displayName: e.target.value }))} />
              </div>
              <button type="button" className="mim-btn mim-btn-primary" disabled={!urlDraft.url || urlDraft.contentTypes.length === 0 || addUrl.isPending} onClick={() => addUrl.mutate()}>
                Save URL
              </button>
            </div>
          </div>
        </div>
      )}

      {showExtractionModal && (
        <div className="mim-modal-backdrop">
          <div className="mim-modal" style={{ maxWidth: 820, maxHeight: '90vh', overflow: 'auto' }}>
            <div className="mim-modal-header">
              <h2>Create extraction report</h2>
              <button type="button" className="mim-icon-button" onClick={() => setShowExtractionModal(false)}><X size={16} /></button>
            </div>
            <div className="mim-modal-body">
              <ExtractionProfileGenerate
                embedded
                productId={productId}
                onComplete={() => {
                  setShowExtractionModal(false)
                  qc.invalidateQueries({ queryKey: ['compliance-preflight', productId] })
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showSchedule && (
        <div className="mim-modal-backdrop">
          <div className="mim-modal">
            <div className="mim-modal-header">
              <h2>Add to scheduler</h2>
              <button type="button" className="mim-icon-button" onClick={() => setShowSchedule(false)}><X size={16} /></button>
            </div>
            <div className="mim-modal-body mim-stack">
              <p className="mim-help-text" style={{ margin: 0 }}>
                After each run, a DOCX report is emailed to the owner. Manage schedules later under Check Scheduler.
              </p>
              <label>
                <span className="mim-label">Schedule name</span>
                <input className="mim-input" placeholder="e.g. KS110 weekly" value={scheduleForm.name} onChange={(e) => setScheduleForm((s) => ({ ...s, name: e.target.value }))} />
              </label>
              <label>
                <span className="mim-label">Report email</span>
                <input className="mim-input" type="email" placeholder="name@kingspan.com" value={scheduleForm.ownerEmail} onChange={(e) => setScheduleForm((s) => ({ ...s, ownerEmail: e.target.value }))} />
              </label>
              <label>
                <span className="mim-label">How often</span>
                <select className="mim-select" value={scheduleForm.frequency} onChange={(e) => setScheduleForm((s) => ({ ...s, frequency: e.target.value }))}>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </label>
              {scheduleForm.frequency === 'Weekly' && (
                <label>
                  <span className="mim-label">Day</span>
                  <select className="mim-select" value={scheduleForm.dayOfWeek} onChange={(e) => setScheduleForm((s) => ({ ...s, dayOfWeek: Number(e.target.value) }))}>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                    <option value={0}>Sunday</option>
                  </select>
                </label>
              )}
              {scheduleForm.frequency === 'Monthly' && (
                <label>
                  <span className="mim-label">Day of month</span>
                  <input
                    className="mim-input"
                    type="number"
                    min={1}
                    max={28}
                    value={scheduleForm.dayOfMonth}
                    onChange={(e) => setScheduleForm((s) => ({ ...s, dayOfMonth: Number(e.target.value) || 1 }))}
                  />
                </label>
              )}
              <div className="mim-grid-2">
                <label>
                  <span className="mim-label">Hour (UTC)</span>
                  <input
                    className="mim-input"
                    type="number"
                    min={0}
                    max={23}
                    value={scheduleForm.hourUtc}
                    onChange={(e) => setScheduleForm((s) => ({ ...s, hourUtc: Number(e.target.value) }))}
                  />
                </label>
                <label>
                  <span className="mim-label">Minute (UTC)</span>
                  <input
                    className="mim-input"
                    type="number"
                    min={0}
                    max={59}
                    value={scheduleForm.minuteUtc}
                    onChange={(e) => setScheduleForm((s) => ({ ...s, minuteUtc: Number(e.target.value) }))}
                  />
                </label>
              </div>
              <button
                type="button"
                className="mim-btn mim-btn-primary"
                disabled={scheduleMut.isPending || !scheduleForm.ownerEmail.trim()}
                onClick={() => scheduleMut.mutate()}
              >
                {scheduleMut.isPending ? 'Saving…' : 'Save schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MimPage>
  )
}
